/**
 * 권한 검사 유틸 (메뉴·라우트 접근 통일)
 * user: { companyType, permissions: string[] }
 */
import { GA_ASSISTANT_PERMISSION } from '../config/fcheros';

/**
 * 단일 권한 보유 여부
 * @param {object|null} user
 * @param {string} permission
 * @returns {boolean}
 */
export function hasPermission(user, permission) {
  if (!user?.permissions || !Array.isArray(user.permissions)) return false;
  const target = String(permission).toLowerCase();
  return user.permissions.some((p) => p && String(p).toLowerCase() === target);
}

/**
 * 권한 중 하나라도 보유 여부
 * @param {object|null} user
 * @param {string[]} permissions
 * @returns {boolean}
 */
export function hasAnyPermission(user, permissions) {
  if (!permissions?.length) return false;
  return permissions.some((p) => hasPermission(user, p));
}

/**
 * 현황판 접근 가능 여부 (GA/IS/OP 타입별 권한)
 * @param {object|null} user
 * @returns {boolean}
 */
export function canAccessDashboard(user) {
  if (!user?.permissions || !Array.isArray(user.permissions)) return false;
  if (user.companyType === 'GA') {
    return hasAnyPermission(user, ['GA_DASHBOARD_SUPERVISOR', 'GA_DASHBOARD_TOPCLASS', 'GA_DASHBOARD_FC']);
  }
  if (user.companyType === 'IS') {
    return hasAnyPermission(user, ['IS_DASHBOARD_DIRECTOR', 'IS_DASHBOARD_MANAGER']);
  }
  if (user.companyType === 'OP') {
    return hasPermission(user, 'OP_DASHBOARD_USER');
  }
  return false;
}

/**
 * 라우트 키별 접근 가능 여부 (메뉴 표시·라우트 가드 공통)
 * @param {object|null} user
 * @param {string} routeKey
 * @returns {boolean}
 */
export function canAccessRoute(user, routeKey) {
  if (!user) return false;
  const perms = user.permissions;
  const isArray = Array.isArray(perms);
  const type = user.companyType;

  switch (routeKey) {
    case 'dashboard':
      return canAccessDashboard(user);
    case 'notices':
      return true; // 로그인 사용자 전체
    case 'events':
      return type === 'GA' && isArray && hasPermission(user, 'EVENT_MANAGE');
    case 'design-management':
      return type === 'GA' && isArray && hasAnyPermission(user, ['GA_DESIGN_CREATE', 'GA_DESIGN_READ']);
    case 'subscription-request-management':
      return type === 'GA' && isArray && hasAnyPermission(user, ['GA_SUBSCRIPTION_CREATE', 'GA_SUBSCRIPTION_READ']);
    case 'subscription-receive-management':
      if (type === 'GA' && isArray)
        return hasAnyPermission(user, ['SUBSCRIPTION_RECEIVE_CREATE', 'SUBSCRIPTION_RECEIVE_READ']);
      if (type === 'IS' && isArray)
        return hasAnyPermission(user, ['SUBSCRIPTION_RECEIVE_CREATE', 'SUBSCRIPTION_RECEIVE_READ', 'SUBSCRIPTION_RECEIVE_READ_ALL']);
      return false;
    case 'customers':
      return type === 'GA' && isArray && hasPermission(user, 'CONTACT_MANAGE');
    case 'memos':
      return type === 'GA' && isArray && hasPermission(user, 'MEMO_MANAGE');
    case 'personal-info-policies':
      return type === 'OP' && isArray && hasPermission(user, 'POLICY_MANAGE');
    case 'service-terms-policies':
      return type === 'OP';
    case 'users':
      return (type === 'IS' || type === 'OP') && isArray && hasPermission(user, 'USER_MANAGE');
    case 'ga-user-management':
      return type === 'GA' && isArray && hasPermission(user, 'USER_MANAGE');
    case 'companies':
      return (type === 'IS' || type === 'OP') && isArray && hasPermission(user, 'COMPANY_MANAGE');
    case 'organizations':
      return isArray && (
        (type === 'OP' && hasPermission(user, 'ORG_MANAGE')) ||
        (type === 'GA' && hasPermission(user, 'GA_ORG_MANAGE'))
      );
    case 'divisions':
    case 'login-logs':
    case 'memo-summary-call-logs':
    case 'resignation-processing-monitor':
      return type === 'OP';
    case 'regions':
      return type === 'OP' && isArray && hasPermission(user, 'REGION_MANAGE');
    case 'role-management':
      return type === 'OP' && isArray && hasPermission(user, 'ROLE_MANAGE');
    case 'ga-role-management':
      return type === 'GA' && isArray && hasPermission(user, 'GA_ROLE_MANAGE');
    case 'permission-management':
      return type === 'OP' && isArray && hasPermission(user, 'PERMISSION_MANAGE');
    case 'design-request-response':
      if (!isArray) return false;
      if (type === 'IS')
        return hasAnyPermission(user, ['DESIGN_RECEIVE_CREATE', 'DESIGN_RECEIVE_READ', 'DESIGN_RECEIVE_READ_ALL']);
      if (type === 'GA')
        return hasAnyPermission(user, ['DESIGN_RECEIVE_CREATE', 'DESIGN_RECEIVE_READ']);
      return false;
    case 'group-mapping-management':
      return type === 'GA' && isArray && hasAnyPermission(user, ['GA_GROUP_MAPPING_MANAGE', 'GA_BRANCH_MANAGE']);
    case 'partner-group-management':
      return type === 'GA' && isArray && hasPermission(user, 'GA_PARTNER_GROUP_MANAGE');
    case 'chat':
      return isArray && hasPermission(user, 'CHAT_MANAGE');
    case 'invitation':
      return type === 'GA' && isArray && hasPermission(user, 'INVITE_MANAGE');
    case 'report-statistics-user':
      return type === 'GA' && isArray && hasPermission(user, 'GA_REPORT_USER');
    case 'report-statistics-all':
      return type === 'GA' && isArray && hasPermission(user, 'GA_REPORT_ALL');
    case 'report-statistics-by-receiver':
    case 'report-statistics-by-receiver-waiting':
    case 'report-statistics-by-receiver-processing':
      return type === 'GA' && isArray && hasPermission(user, 'GA_REPORT_ALL');
    case 'report-statistics-by-ga-receiver':
      return type === 'GA' && isArray && hasPermission(user, 'GA_REPORT_ALL');
    case 'report-statistics-is-admin':
      return type === 'IS' && isArray && hasPermission(user, 'IS_REPORT_ALL');
    case 'system-settings':
      return type === 'OP' && isArray && hasPermission(user, 'SYSTEM_SETTING');
    case 'llm-settings':
      return type === 'OP';
    case 'code-management':
      return type === 'OP';
    case 'promotion-manage':
      return type === 'OP' && isArray && hasPermission(user, 'promotion_manage');
    case 'promotion-rate-manage':
      return type === 'OP' && isArray && hasPermission(user, 'promotion_manage');
    case 'promotion-read':
      return type === 'IS' && isArray && hasPermission(user, 'promotion_read');
    case 'ga-assistant':
      return isArray && hasPermission(user, GA_ASSISTANT_PERMISSION);
    default:
      return false;
  }
}
